import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { z } from 'zod';
import { useChangePassword } from '../hooks/auth-actions.js';
import { describeError, mergeFormClasses } from './shared.js';
const schema = z.object({
    currentPassword: z.string().min(1, 'Enter your current password.'),
    newPassword: z.string().min(8, 'Use at least 8 characters.'),
    confirmPassword: z.string().min(1, 'Re-enter the new password.'),
});
/**
 * Lets a signed-in user replace their password. Confirms the current password
 * against the server, runs zod validation locally, and surfaces a single
 * success message on completion (rather than redirecting — the host app owns
 * post-success behavior).
 */
export function ChangePasswordForm({ onSuccess, classNames, errorMessages, }) {
    const classes = mergeFormClasses(classNames);
    const action = useChangePassword();
    const [currentPassword, setCurrent] = React.useState('');
    const [newPassword, setNew] = React.useState('');
    const [confirmPassword, setConfirm] = React.useState('');
    const [validationError, setValidationError] = React.useState(null);
    const [completed, setCompleted] = React.useState(false);
    const error = describeError(action.error, errorMessages);
    const handleSubmit = async (event) => {
        event.preventDefault();
        // **And it stops here** (`USR-004` AC-3). A `submit` event bubbles, and every host renders
        // this component *inside* another form: `my-account-page.tsx` wraps the whole screen in one,
        // and so do the organizer and admin account pages. Without this, pressing *Update password*
        // ran the enclosing form's `onSubmit` as well — a member changing their password also wrote
        // their account record, silently, with whatever was in the draft at the time. Seen as a
        // `PUT /api/members/me/account` beside the password change in
        // `apps/web/__tests__/members/a-member-can-change-their-password.test.tsx`.
        //
        // `preventDefault` alone does not do this: it suppresses the browser's own navigation, not
        // React's synthetic propagation to an ancestor handler.
        event.stopPropagation();
        const parsed = schema.safeParse({ currentPassword, newPassword, confirmPassword });
        if (!parsed.success) {
            setValidationError(parsed.error.issues[0]?.message ?? 'Enter all fields.');
            return;
        }
        if (parsed.data.newPassword !== parsed.data.confirmPassword) {
            setValidationError('New passwords do not match.');
            return;
        }
        setValidationError(null);
        try {
            await action.run({
                currentPassword: parsed.data.currentPassword,
                newPassword: parsed.data.newPassword,
            });
            setCompleted(true);
            setCurrent('');
            setNew('');
            setConfirm('');
            onSuccess?.();
        }
        catch {
            // Captured by hook.
        }
    };
    return (_jsxs("form", { className: classes.form, onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-change-current", children: "Current password" }), _jsx("input", { id: "auth-ui-change-current", className: classes.input, type: "password", autoComplete: "current-password", required: true, value: currentPassword, onChange: (event) => setCurrent(event.target.value) })] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-change-new", children: "New password" }), _jsx("input", { id: "auth-ui-change-new", className: classes.input, type: "password", autoComplete: "new-password", required: true, value: newPassword, onChange: (event) => setNew(event.target.value) })] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-change-confirm", children: "Confirm new password" }), _jsx("input", { id: "auth-ui-change-confirm", className: classes.input, type: "password", autoComplete: "new-password", required: true, value: confirmPassword, onChange: (event) => setConfirm(event.target.value) })] }), validationError !== null ? (_jsx("p", { className: classes.error, role: "alert", children: validationError })) : null, error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, completed ? (_jsx("p", { className: classes.success, role: "status", children: "Password updated." })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: action.loading, children: action.loading ? 'Updating…' : 'Update password' })] }));
}
//# sourceMappingURL=ChangePasswordForm.js.map